import React from 'react';

import type { ErrorProps } from '@components/ErrorView';
import { useAuthentication } from '@context/AuthContext';
import { listenToBlockedUserIds } from '@db/Users/Block';
import { listenToUser } from '@db/Users/Listen';
import { useUserOnboardingStatusQuery } from '@db/Users/Queries';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { logError } from '@helpers/Loggers';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { auth } from '@helpers/firebase/Initialize';
import { User } from '@types';

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;

export type CurrentUserContextProps = {
  blockedUserIds: Set<string>;
  userDoc?: User;
};

export const CurrentUserContext = createNamedContext<CurrentUserContextProps>({
  CurrentUserContext: undefined,
});

export function useCurrentUser() {
  return useRequiredContext(CurrentUserContext);
}

export function CurrentUserProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const [userDoc, setUserDoc] = React.useState<User | undefined>(undefined);
  const [blockedUserIds, setBlockedUserIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (loggedIn) {
      return listenToUser(
        getCurrentUserId(),
        (user) => setUserDoc(user),
        (error) => logError(error)
      );
    }
  }, [loggedIn]);

  React.useEffect(() => {
    if (loggedIn) {
      return listenToBlockedUserIds(
        (blockedUserIds) => setBlockedUserIds(blockedUserIds),
        (error) => logError(error)
      );
    }
  }, [loggedIn]);

  const value = {
    blockedUserIds,
    userDoc,
  };

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

export type OnboardingStatusContextProps = {
  loading: boolean;
  onboarded: boolean;
  setOnboarded: SetState<boolean>;
  userDocExists: boolean;
  error: boolean;
  errorProps?: ErrorProps;
};

export const OnboardingStatusContext = createNamedContext<OnboardingStatusContextProps>({
  OnboardingStatusContext: undefined,
});

export function useOnboardingStatus() {
  return useRequiredContext(OnboardingStatusContext);
}

type LoadedOnboardingStatus = {
  onboarded: boolean;
  userDocExists: boolean;
  userId?: string;
};

type SessionOnboardingStatus = {
  onboarded: boolean;
  userId?: string;
};

export function OnboardingStatusProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const [loadedOnboardingStatus, setLoadedOnboardingStatus] =
    React.useState<LoadedOnboardingStatus>();
  const [sessionOnboardingStatus, setSessionOnboardingStatus] =
    React.useState<SessionOnboardingStatus>();
  const userId = auth.currentUser?.uid;
  const onboardingStatusQuery = useUserOnboardingStatusQuery({
    enabled: loggedIn,
    userId,
  });

  const waitingForAuthenticatedUser = loggedIn && !userId;
  // Capture the startup doc check once; later onboarding progress is explicit via setOnboarded.
  if (
    loggedIn &&
    userId &&
    loadedOnboardingStatus?.userId !== userId &&
    !onboardingStatusQuery.loading &&
    !onboardingStatusQuery.error
  ) {
    setLoadedOnboardingStatus({
      onboarded: onboardingStatusQuery.onboarded,
      userDocExists: onboardingStatusQuery.userDocExists,
      userId,
    });
  }

  const loadedOnboardingStatusForCurrentUser =
    loadedOnboardingStatus?.userId === userId ? loadedOnboardingStatus : undefined;
  const sessionOnboardingStatusForCurrentUser =
    sessionOnboardingStatus?.userId === userId ? sessionOnboardingStatus : undefined;
  const onboarded =
    sessionOnboardingStatusForCurrentUser?.onboarded ??
    loadedOnboardingStatusForCurrentUser?.onboarded ??
    false;
  const setOnboarded = React.useCallback<SetState<boolean>>(
    (nextOnboarded) => {
      setSessionOnboardingStatus((currentSessionStatus) => {
        const sessionStatusForUser =
          currentSessionStatus?.userId === userId ? currentSessionStatus : undefined;
        const loadedStatusForUser =
          loadedOnboardingStatus?.userId === userId ? loadedOnboardingStatus : undefined;
        const currentOnboarded =
          sessionStatusForUser?.onboarded ??
          loadedStatusForUser?.onboarded ??
          onboardingStatusQuery.onboarded;
        if (typeof nextOnboarded === 'function') {
          return { onboarded: nextOnboarded(currentOnboarded), userId };
        }

        return { onboarded: nextOnboarded, userId };
      });
    },
    [loadedOnboardingStatus, onboardingStatusQuery.onboarded, userId]
  );

  const value = {
    loading:
      waitingForAuthenticatedUser ||
      (loggedIn && !loadedOnboardingStatusForCurrentUser && !onboardingStatusQuery.error),
    onboarded,
    setOnboarded,
    userDocExists: loadedOnboardingStatusForCurrentUser?.userDocExists ?? false,
    error: onboardingStatusQuery.error,
    errorProps: onboardingStatusQuery.errorProps,
  };

  return (
    <OnboardingStatusContext.Provider value={value}>{children}</OnboardingStatusContext.Provider>
  );
}
